import React, { useState } from 'react';
import { ExclamationTriangleIcon, ArrowLeftIcon, Cog6ToothIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import ControlModelSetup from './ControlModelSetup';
import DigitalInputSetup from './DigitalInputSetup';

const AdvancedSettings = ({ getSettingValue, handleEdit, onBack }) => {
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);

  // Two entries, as on the panel. What used to be a separate Beta Update page
  // is part of Control Model Setup now.
  const menuOptions = [
    {
      id: 'control_model',
      title: 'Control model setup',
      description: 'Moisture window, rate bias, model readouts',
      component: ControlModelSetup
    },
    {
      id: 'digital_input',
      title: 'Digital input setup',
      description: 'Configure digital input parameters',
      component: DigitalInputSetup
    }
  ];
  
  const handleConfirm = () => {
    setShowConfirmation(false);
  };
  
  const handleCancel = () => {
    onBack();
  };
  
  const handleBack = () => {
    if (selectedOption) {
      setSelectedOption(null);
    } else if (!showConfirmation) {
      setShowConfirmation(true);
    } else {
      onBack();
    }
  };
  
  // Show confirmation dialog
  if (showConfirmation) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex items-center justify-center h-full"
        >
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-10 h-10 text-yellow-600" />
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 text-center mb-4">
              Advanced Settings Warning
            </h3>
            
            <p className="text-gray-600 text-center mb-8">
              You are about to enter Advanced Settings mode. Changing these settings may affect system operation.
              Please proceed with caution.
            </p>
            
            <div className="flex gap-4">
              <button
                onClick={handleCancel}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }
  
  // If an option is selected, show that component
  if (selectedOption) {
    const SelectedComponent = selectedOption.component;
    return (
      <div className="h-full flex flex-col">
        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <h3 className="text-xl font-bold text-gray-800">{selectedOption.title}</h3>
        </div>
        
        {/* Component Content */}
        <div className="flex-1 overflow-y-auto">
          <SelectedComponent
            getSettingValue={getSettingValue}
            handleEdit={handleEdit}
          />
        </div>
      </div>
    );
  }
  
  // Show main menu
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
        </button>
        <h3 className="text-xl font-bold text-gray-800">Advanced Settings</h3>
      </div>
      
      {/* Menu Options */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto space-y-4">
          {menuOptions.map((option) => (
            <motion.button
              key={option.id}
              onClick={() => setSelectedOption(option)}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.99 }}
              className="relative w-full bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden text-left flex items-center gap-4 p-5"
            >
              <div className="w-12 h-12 rounded-lg bg-[#2a4e7c]/10 flex items-center justify-center shrink-0">
                <Cog6ToothIcon className="w-6 h-6 text-[#2a4e7c]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold text-gray-800 capitalize">{option.title}</p>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 shrink-0" />
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2a4e7c]" />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;